// SCR-09 glossary (plan 9.5): ~180 terms fetched at once and searched in the browser; per-term URL by slug;
// old slugs resolve to the canonical one; editing (glossary.edit) keeps versions and is audited; weekly link check.
import { z } from 'zod';
import { GlossaryCategory, LinkStatus } from '../enums.js';
import { GLOSSARY_RULES } from '../controls.js';
import { Count, EpochMs, LocalizedText, PersonRef, RoleRef } from '../common.js';

export const Slug = z.string().regex(GLOSSARY_RULES.slugPattern).max(GLOSSARY_RULES.slugMax);
export const SlugParams = z.object({ slug: z.string().toLowerCase().max(80) });

/** External explanation link per language (https only; checked weekly: HTTP 200 + expected title + no "not found" marker). */
export const GlossaryLink = z.object({
  url: z.string().regex(GLOSSARY_RULES.httpsUrl).max(GLOSSARY_RULES.urlMax),
  sourceName: z.string().max(80),
  /** Language of the linked document (en → "(영문)"). */
  docLang: z.enum(['ko', 'en']),
  expectedTitle: z.string().max(200),
  status: LinkStatus,
  checkedAt: EpochMs.nullable(),
});

export const GlossaryTerm = z.object({
  slug: Slug,
  aliases: z.array(z.string().max(80)),
  oldSlugs: z.array(z.string().max(80)),
  category: GlossaryCategory,
  core: z.boolean(),
  /** Linked label-dictionary key: the plain word then comes from the label dictionary and is not editable here. */
  labelKey: z.string().nullable(),
  term: LocalizedText,
  plain: LocalizedText,
  def: LocalizedText,
  example: LocalizedText.nullable(),
  rawNames: z.array(z.string().max(120)),
  related: z.array(Slug),
  /** Hash routes where the term appears (e.g. `#/stats?item=usage`). */
  screens: z.array(z.string().max(120)),
  links: z.object({ ko: GlossaryLink.nullable(), en: GlossaryLink.nullable() }),
  manualRef: z.string().max(200).nullable(),
  manualStatus: z.enum(['ok', 'pending']).nullable(),
  /** Hidden terms are listed only for editors; a direct link still opens them with a notice. */
  hidden: z.boolean(),
  version: z.int().positive(),
  updatedAt: EpochMs,
});
export type GlossaryTerm = z.infer<typeof GlossaryTerm>;

/**
 * GET /api/v1/glossary — every visible term + category counts + link-check summary. Everyone sees the last/next check
 * times in the footer (mockup glossary.js); `bad` (links needing a look) is a number for editors only, else null.
 */
export const GlossaryListResponse = z.object({
  terms: z.array(GlossaryTerm),
  categories: z.array(z.object({ id: GlossaryCategory, count: Count, core: Count })),
  linkCheck: z.object({ lastRunAt: EpochMs.nullable(), nextRunAt: EpochMs.nullable(), bad: Count.nullable() }),
});
/** GET /api/v1/glossary/:slug — resolves old slugs (`canonical=false` → web rewrites the address). Unknown → 404. */
export const GlossaryTermResponse = z.object({ term: GlossaryTerm, canonical: z.boolean() });

const Text = (max: number, min = 0) => z.string().trim().min(min).max(max).refine((s) => !GLOSSARY_RULES.forbiddenText.test(s), 'plain text only (no HTML, no raw links)');
const LText = (max: number, min = 0) => z.object({ ko: Text(max, min), en: Text(max, min) });
const LinkInput = z.object({ url: z.string().regex(GLOSSARY_RULES.httpsUrl).max(GLOSSARY_RULES.urlMax), sourceName: z.string().trim().max(80), expectedTitle: z.string().trim().max(200) });

/** PATCH /api/v1/glossary/:slug — editable fields only; slug never changes; label-linked plain words are locked. */
export const GlossaryEditRequest = z.object({
  expectedVersion: z.int().positive(),
  term: LText(GLOSSARY_RULES.shortMax, 1).optional(),
  /** Plain word and definition can never be emptied (mockup editor requires both). */
  plain: LText(GLOSSARY_RULES.shortMax, 1).optional(),
  def: LText(GLOSSARY_RULES.longMax, 1).optional(),
  example: LText(GLOSSARY_RULES.longMax).nullable().optional(),
  aliases: z.array(z.string().trim().max(80)).max(30).optional(),
  related: z.array(Slug).max(12).optional(),
  screens: z.array(z.string().max(120)).max(20).optional(),
  links: z.object({ ko: LinkInput.nullable(), en: LinkInput.nullable() }).partial().optional(),
  category: GlossaryCategory.optional(),
  core: z.boolean().optional(),
  manualRef: z.string().max(200).nullable().optional(),
});
/** POST /api/v1/glossary — add a term (slug immutable afterwards; must not collide with any slug/old slug). */
export const GlossaryAddRequest = z.object({
  slug: Slug,
  category: GlossaryCategory,
  core: z.boolean().default(false),
  term: LText(GLOSSARY_RULES.shortMax, 1),
  plain: LText(GLOSSARY_RULES.shortMax, 1),
  def: LText(GLOSSARY_RULES.longMax, 1),
  example: LText(GLOSSARY_RULES.longMax).nullable().default(null),
  aliases: z.array(z.string().trim().max(80)).max(30).default([]),
  related: z.array(Slug).max(12).default([]),
  screens: z.array(z.string().max(120)).max(20).default([]),
  links: z.object({ ko: LinkInput.nullable(), en: LinkInput.nullable() }).default({ ko: null, en: null }),
  manualRef: z.string().max(200).nullable().default(null),
});
export const GlossaryTermWriteResponse = z.object({ term: GlossaryTerm, linkCheckQueued: z.boolean() });

export const GLOSSARY_VERSION_KINDS = ['seed', 'add', 'edit', 'revert', 'hide', 'unhide'] as const;
export const GlossaryHistoryResponse = z.object({
  items: z.array(z.object({ version: z.int().positive(), at: EpochMs, actor: PersonRef.nullable(), role: RoleRef.nullable(), kind: z.enum(GLOSSARY_VERSION_KINDS), fields: z.array(z.string()), current: z.boolean() })),
});
export const GlossaryRevertRequest = z.object({ version: z.int().positive() });

export const LINK_FAILURE_REASONS = ['http_error', 'timeout', 'title_mismatch', 'not_found_marker', 'redirected', 'not_checked'] as const;
/** GET /api/v1/admin/glossary/link-check — last weekly run (editors). POST on the same path runs it now (job). */
export const LinkCheckReport = z.object({
  lastRunAt: EpochMs.nullable(),
  nextRunAt: EpochMs.nullable(),
  total: Count,
  ok: Count,
  bad: Count,
  pending: Count,
  failures: z.array(z.object({ slug: Slug, lang: z.enum(['ko', 'en', 'manual']), url: z.string(), status: LinkStatus, httpStatus: z.int().nullable(), reason: z.enum(LINK_FAILURE_REASONS), checkedAt: EpochMs.nullable() })),
});

export const glossarySchemas = {
  SlugParams,
  GlossaryTerm,
  GlossaryListResponse,
  GlossaryTermResponse,
  GlossaryEditRequest,
  GlossaryAddRequest,
  GlossaryTermWriteResponse,
  GlossaryHistoryResponse,
  GlossaryRevertRequest,
  LinkCheckReport,
} as const;
